import { Injectable, Query } from '@angular/core';
import {
  AssetType,
  IAddresses,
  IFirebaseLogin,
  IGeneralCodeDescriptionType,
  IQuoteAssets,
  IQuoteErrorRepsonse,
  IQuoteRequest,
  IUser,
  IUserResetPassword,
  QuoteRequestStatusType,
  QuoteRequestType,
} from '@takectrl-multiapp/svcs-share-config';
import { LoadingController } from '@ionic/angular';
import {
  DateService,
  IEmailAuth,
  IInsuranceHistory,
  IPersonalDetails,
  IQuestion,
  IReferralMailPayload,
  IUserAllRelatedData,
  SharedAPIDataCalculationService,
  SharedConfig,
  SharedDialogService,
  UserFactory,
} from '@takectrl-multiapp/svcs-share-config';
import { APIMailService, APIQuestionService, APIQuoteScheduleService, APIUserService, ApiAuthService } from '@takectrl-multiapp/svcs-ctrl-api';
import { ReqResponse } from '@takectrl-multiapp/svcs-share-config';
import { ICtrlResponse } from 'libs/svcs-ctrl-api/src/lib/interfaces/api.interface';
import { BUSNQuestionService } from './busn-question.service';
import { BUSNQuoteScheduleService } from './busn-quote-schedule.service';
import { APIHttpService } from 'libs/svcs-ctrl-api/src/lib/services/api-http.service';
import { firstValueFrom, take } from 'rxjs';
import { BUSNThirdPartyService } from './busn-third-party.service';
import { SharedConfigService } from 'libs/svcs-share-config/src/lib/services/shared-config.service';
import { FirebaseService } from '@takectrl-multiapp/svcs-core';
import { ApiCacheService } from 'libs/svcs-ctrl-api/src/lib/services/api-cache.service';
import { ROUTER_CONFIGURATION } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class BUSNUserService {
  public submittedForm = false;
  subscriberHitTimeout = 0;
  private activeLoaders: Set<string> = new Set();
  private assetsCompleteInProgress: Promise<void> | null = null;
  private readonly LOADER_TYPES = {
    USER_DETAILS: 'subscribeToUserDetails',
    QUOTE_REQUEST: 'subscribeToQuoteRequest',
    ALL_USER_DATA: 'setAllUserRelatedData',
  };
  constructor(
    public loadingCtrl: LoadingController,
    public apiQuestionService: APIQuestionService,
    public apiUserService: APIUserService,
    public apiAuthService: ApiAuthService,
    public dateService: DateService,
    private readonly sharedQuoteScheduleService: SharedAPIDataCalculationService,
    public busnQuestionService: BUSNQuestionService,
    public busnQuoteScheduleService: BUSNQuoteScheduleService,
    private readonly sharedDialogService: SharedDialogService,
    public sharedConfig: SharedConfig,
    public apiCacheService: ApiCacheService,
    public firebaseService: FirebaseService,

    public sharedConfigService: SharedConfigService,
    public apiQuoteScheduleService: APIQuoteScheduleService,
    public busnThirdPartyService: BUSNThirdPartyService,
    private readonly apiMailService: APIMailService,
    private readonly httpService: APIHttpService
  ) {}

  private startLoader(loaderType: string): void {
    this.activeLoaders.add(loaderType);
    this.sharedConfig.setShowCustomLoader(true, loaderType);
  }

  private endLoader(loaderType: string): void {
    this.activeLoaders.delete(loaderType);
    this.sharedConfig.setShowCustomLoader(false, loaderType);
  }

  // TODO REFACTOR REMOVE PROMISES
  async getUser(firebase_uuid: string): Promise<IUser> {
    const userResult = await firstValueFrom(this.apiUserService.getUser(firebase_uuid));
    return userResult.results;
  }

  async getPermissions() {
    const permissions = await firstValueFrom(this.apiUserService.getUserPermissions());
    return permissions.results;
  }
  
  async getUserAddresses(): Promise<Array<IAddresses>> {
    const results = (await firstValueFrom(this.apiUserService.getUserAddresses())).results;
    return Array.isArray(results) ? results : [];
  }

  async getAddressBasedOnID(addressId: any): Promise<IAddresses> {
    const results = (await firstValueFrom(this.apiUserService.getAddressBasedOnID(addressId))).results;
    return results;
  }

  async getUserByEmail(email: string): Promise<IUser> {
    return (await firstValueFrom(this.apiUserService.getUserByEmail(email))).results;
  }

  async updateUser(user: IUser): Promise<void> {
    await firstValueFrom(this.apiUserService.updateUser(user));
  }

  async addressProvinces(): Promise<Array<IGeneralCodeDescriptionType>> {
    const results = (await firstValueFrom(this.apiUserService.provinces())).results;
    return Array.isArray(results) ? results : [];
  }

  async addressEstates(search: string) {
    const results = (await firstValueFrom(this.apiUserService.estates(search))).results;
    return Array.isArray(results) ? results : [];
  }

  async addressSectionalTitles(search: string) {
    const results = (await firstValueFrom(this.apiUserService.sectionalTitles(search))).results;
    return Array.isArray(results) ? results : [];
  }

  async addressStreet(suburb_code: string) {
    const results = (await firstValueFrom(this.apiUserService.street(suburb_code))).results;
    return Array.isArray(results) ? results : [];
  }

  async addressSuburb(province_code: string) {
    const results = (await firstValueFrom(this.apiUserService.suburb(province_code))).results;
    return Array.isArray(results) ? results : [];
  }

  async updateFCMToken(token: string): Promise<IUser> {
    this.sharedConfig.user.account_details.fcm_token = token;
    return (await firstValueFrom(this.apiUserService.updateUser(this.sharedConfig.user))).results;
  }

  async createUser(
    email: string,
    referralCode: string,
    hard_of_hearing: boolean,
    popiaConsentGiven = false,
    popia_consent_message = ''
  ): Promise<ICtrlResponse<IUser>> {
    const user: IUser = UserFactory.initUser(
      email,
      referralCode,
      hard_of_hearing,
      popiaConsentGiven,
      popia_consent_message,
      this.sharedConfig.appSettings.referral_code_prefix +
        Math.random().toString(36).substring(3).slice(0, 3).toUpperCase()
    );
    const userResponse = await firstValueFrom(this.apiUserService.createUser(user));
    return userResponse;
  }

  async createLead(): Promise<any> {
    return (await firstValueFrom(this.apiUserService.createLead()))
  }

  async updatePersonalDetails(personalDetails: IPersonalDetails) {
    await firstValueFrom(this.apiUserService.updatePersonalDetails(personalDetails));
  }

  async updateUserInsuranceHistory(insuranceHistory: IInsuranceHistory) {
    await firstValueFrom(this.apiUserService.updateUserInsuranceHistory(insuranceHistory));
  }

  async createUserInsuranceHistory(insurance_history: IInsuranceHistory = this.sharedConfig.user.insurance_history): Promise<IInsuranceHistory> {
    return (await firstValueFrom(this.apiUserService.createUserInsuranceHistory(insurance_history))).results as IInsuranceHistory;
  }

  async updateUserInsuranceHistories(questions: Array<IQuestion>) {
    if (!this.sharedConfig.user.insurance_history) this.sharedConfig.user.insurance_history = {};
    for (const question of questions) {
      for (const underwritingAnswer of question.underwriting_answers) {
        this.sharedConfig.user.insurance_history[underwritingAnswer.object_field] = underwritingAnswer.object_answer;
      }
    }
    this.updateUserInsuranceHistory(this.sharedConfig.user.insurance_history);
  }

  async updateUserDetailsAnswers(questions: Array<IQuestion>) {
    for (const question of questions) {
      for (const underwritingAnswer of question.underwriting_answers) {
        this.sharedConfig.user.personal_details[underwritingAnswer.object_field] = underwritingAnswer.object_answer;
      }
    }
    this.updatePersonalDetails(this.sharedConfig.user.personal_details);
  }

  async changeEmailAddress(new_email: string): Promise<ICtrlResponse<unknown>> {
    return await firstValueFrom(this.apiUserService.updateUserEmail(new_email));
  }

  changePassword(oldPassword: string, newPassword: string): void {
    this.busnThirdPartyService.firebaseChangePassword(oldPassword, newPassword);
  }

  async resetPassword(userEmail: string): Promise<IUserResetPassword> {
    return await firstValueFrom(this.apiUserService.resetPassword(userEmail));
  }

  async setUserPassword(uid: string, email: string, new_password: string): Promise<ReqResponse> {
    const url = `${SharedConfig.appCredentials.firebase_functions_url}/authentication/update-user-password`;
    return (await firstValueFrom(
      this.httpService.post(url, {
        uid,
        email,
        new_password,
      })
    )) as ReqResponse;
  }

  async checkEmailForSpam(email: string): Promise<IEmailAuth> {
    if (!this.sharedConfig.appSettings?.validate_email_for_spam_enabled) {
      return {
        pass_validation: true,
        email: email,
        success: true,
      };
    } else {
      return await firstValueFrom(this.apiUserService.checkEmailForSpam(email)).then((data) => {
        return data;
      });
    }
  }

  async registerUser(email: string, password: string): Promise<IFirebaseLogin> {
    return this.busnThirdPartyService.firebaseSignUpWithEmailAndPassword(email, password);
  }

  async signOut(): Promise<void> {
    SharedConfig.clearSharedConfig();
    this.apiCacheService.clear();
    await this.firebaseService.signOut();
  }

  async deleteUserAccount(): Promise<ICtrlResponse<unknown>> {
    return await firstValueFrom(this.apiUserService.deleteUserAccount());
  }

  async checkIfUserMsisdnIsRegistered(phoneNumber: string): Promise<ReqResponse> {
    return await firstValueFrom(this.apiUserService.checkIfUserMsisdnIsRegistered(phoneNumber));
  }

  async referFriend(name: string, surname: string, email: string) {
    this.apiUserService.addReferral(name, surname, email);
  }

  async newReferralEmail(name: string, surname: string, email: string): Promise<ICtrlResponse<unknown>> {
    const payload: IReferralMailPayload = {
      user_id: this.sharedConfig.user.personal_details.user_id,
      email: email,
      preferred_name: name,
      surname: surname,
    };
    return await firstValueFrom(this.apiMailService.newReferralEmail(payload));
  }

  subscribeToFirebaseSubscriptionWatch(userId: number): void {
    if (!SharedConfig.activeSubscriptions.has(userId.toString() + 'setAllUserRelatedDataSubscriber')) {
      SharedConfig.activeSubscriptions.add(userId.toString() + 'setAllUserRelatedDataSubscriber');
      this.busnThirdPartyService.firebaseDatabaseSubscribe(`subscriptions/${userId}`, async (_snapshot) => {
      });
    }

    if (!SharedConfig.activeSubscriptions.has(userId.toString() + 'subscribeToQuoteRequest')) {
      SharedConfig.activeSubscriptions.add(userId.toString() + 'subscribeToQuoteRequest');
      this.subscribeToQuoteRequest(userId);
      this.subscribeToQuotes(userId);
    }

    if (!SharedConfig.activeSubscriptions.has(userId.toString() + 'subscribeToSchedules')) {
      SharedConfig.activeSubscriptions.add(userId.toString() + 'subscribeToSchedules');
      this.subscribeToSchedules(userId);
    }

    if (!SharedConfig.activeSubscriptions.has(userId.toString() + 'subscribeToUserDetails')) {
      SharedConfig.activeSubscriptions.add(userId.toString() + 'subscribeToUserDetails');
      this.busnThirdPartyService.firebaseDatabaseSubscribe(`subscriptions/${userId}/users`, async (_snapshot) => {
        SharedConfig.underwritingQuestionsAll = SharedConfig.underwritingQuestionsAll ? SharedConfig.underwritingQuestionsAll : await this.busnQuestionService.getUnderwritingQuestions();
        await this.doUserQuestionCheck();
      });
    }
  }

  async doUserQuestionCheck() {
    try {
      const user: IUser = await this.getUser(SharedConfig.firebaseUserKey);

      if (user.personal_details) {

        if (!user.personal_details.is_completed) {
          const isAssetQuestionsUserPersonalDetailsComplete = await this.busnQuestionService.isAssetQuestionsComplete(
            AssetType.UserPersonalDetails,
            user.personal_details,
            user,
            user.personal_details_id
          );
          if (isAssetQuestionsUserPersonalDetailsComplete !== user.personal_details.is_completed) {
            // update user
            user.personal_details.is_completed = isAssetQuestionsUserPersonalDetailsComplete;
            firstValueFrom(this.apiUserService.updatePersonalDetails(user.personal_details));
            this.sharedConfig.user.personal_details.is_completed = isAssetQuestionsUserPersonalDetailsComplete;
          }
        }

        if (!user.insurance_history.is_completed) {
          const isAssetQuestionsUserInsuranceDetailsComplete = await this.busnQuestionService.isAssetQuestionsComplete(
            AssetType.UserInsuranceHistory,
            user.insurance_history,
            user,
            user.insurance_history_id
          );

          if (isAssetQuestionsUserInsuranceDetailsComplete !== user.insurance_history.is_completed) {
            user.insurance_history.is_completed = isAssetQuestionsUserInsuranceDetailsComplete;
            // update user
            firstValueFrom(this.apiUserService.updateUserInsuranceHistory(user.insurance_history));
            this.sharedConfig.user.insurance_history.is_completed = isAssetQuestionsUserInsuranceDetailsComplete;
          }
        }

      }
    } catch (error) {
      console.error(error);
    }
  }

  subscribeToSchedules(userId: number) {
    // Perform the Firebase subscription
    this.busnThirdPartyService.firebaseDatabaseSubscribe(`subscriptions/${userId}/schedules`, async (_snapshot) => {
      await this.doScheduleCheck();
    });
  }

  async doScheduleCheck() {
    try {
      setTimeout(async () => {
        await this.setSchedules()
        // this.showOrHideOnTrackPopup(userAllRelatedData);
      }, 3000);
    } catch (error) {
      console.log(error);
    }
  }

  subscribeToQuoteRequest(userId: number) {
    // Perform the Firebase subscription
    this.busnThirdPartyService.firebaseDatabaseSubscribe(`subscriptions/${userId}/quote_requests`, async (_snapshot) => {
      await this.doQuoteRequestDetailsRun();
    });
  }

  subscribeToQuotes(userId: number) {
    // Perform the Firebase subscription
    this.busnThirdPartyService.firebaseDatabaseSubscribe(`subscriptions/${userId}/quotes`, async (_snapshot) => {
      await this.doQuotesRun();
    });
  }

  async doQuotesRun() {
    if (this.sharedConfig.quoteRequest) {
      const currentQuoteRequest = await this.busnQuoteScheduleService.getQuoteRequestById(SharedConfig.user.id, this.sharedConfig.quoteRequest.id);
      if (currentQuoteRequest) {
          this.sharedConfig.setQuoteRequest(currentQuoteRequest);
          this.sharedConfigService.setQuoteRequest(currentQuoteRequest);
      }
    }
  }

  async doQuoteRequestDetailsRun() {
    try {
      const user_current_quote_requests: Array<IQuoteRequest> = await this.busnQuoteScheduleService.getQuoteRequestsCurrent()
      if (!this.sharedConfig.quoteRequest) {
        this.startLoader(this.LOADER_TYPES.QUOTE_REQUEST);
      }

      if (user_current_quote_requests && user_current_quote_requests.length > 0) {
        const latestQuoteRequest =
          user_current_quote_requests?.length > 0
            ? user_current_quote_requests.reduce((prev, current) => (prev.id > current.id ? prev : current))
            : null;
        const completeQuoteRequest = await this.busnQuoteScheduleService.getQuoteRequestById(SharedConfig.user.id, latestQuoteRequest.id);
        if (completeQuoteRequest) {
          this.sharedConfig.setQuoteRequest(completeQuoteRequest);
          this.sharedConfigService.setQuoteRequest(completeQuoteRequest);
          const hasSeenErrorPopup = localStorage.getItem('hasSeenErrorPopup');
          if (
            completeQuoteRequest.status === QuoteRequestStatusType.Failed &&
            !SharedConfig.acknowledgedQuoteRequestError &&
            hasSeenErrorPopup === 'false'
          ) {
            const activeInsurerProviders = SharedConfig.insurers().filter(i => i.active).map(i => i.provider);
            const onlyPremiaActive = activeInsurerProviders.length > 0 && activeInsurerProviders.every(p => p === 'premia');
            if(!onlyPremiaActive) {
              if (completeQuoteRequest.type === QuoteRequestType.Quote) {
                SharedConfig.headerForPopupWithChat = 'No quotes returned';
                SharedConfig.messageForPopupWithChat =
                  'We are aware that you did not receive any quotes. You will be contacted shortly for assistance.';
              } else {
                SharedConfig.headerForPopupWithChat = 'No endorsements returned';
                SharedConfig.messageForPopupWithChat =
                  'We are aware that you did not receive any endorsements. You will be contacted shortly for assistance.';
              }
              SharedConfig.showPopupWithChat = true;
              SharedConfig.acknowledgedQuoteRequestError = true;
              localStorage.setItem('hasSeenErrorPopup', 'true');
            }
          } else {
            await this.busnQuestionService.setIsCompleteFlagOnAllAssetsIfCompleted(this.sharedConfig.user, this.sharedConfig.quoteRequest);
          }
        }
      } else {
        delete SharedConfig.quoteRequest;
      }
      await this.checkIfAssetsAreComplete(this.sharedConfig.quoteRequest?.assets)
      console.log(this.sharedConfig.quoteRequest)
      const assetTypes = ['all_risks', 'buildings', 'caravans', 'contents', 'motorcycles', 'specified_items', 'vehicles'];
      const assetsComplete = assetTypes.every(
        (type) => !this.sharedConfig.quoteRequest?.assets?.[type]?.some((asset: { is_completed: any }) => !asset.is_completed)
      );
      this.sharedConfigService.liveVariables.isQuoteRequestCompletedAndCanGoNext = assetsComplete;

      if (!this.sharedConfig.quoteRequest) {
        this.endLoader(this.LOADER_TYPES.QUOTE_REQUEST);
      }
    } catch (error) {
      this.endLoader(this.LOADER_TYPES.QUOTE_REQUEST);
    }
  }

  async checkIfAssetsAreComplete(assets?: IQuoteAssets) {
    if (!assets) return;
    if (this.assetsCompleteInProgress) return this.assetsCompleteInProgress;

    const checkGroup = (grouped_assets: { id?: number; is_completed?: boolean }[] | undefined, assetType: AssetType) => {
      if (!grouped_assets || grouped_assets.length === 0) return Promise.resolve();
      return Promise.all(
        grouped_assets.map(async (asset) => {
          if (!asset.is_completed) {
            const complete = (await this.busnQuestionService.getProgressCheckerQuestions(assetType, asset.id!)).progress?.progress;
            asset.is_completed = complete === 1;
          }
        })
      );
    };

    this.assetsCompleteInProgress = Promise.all([
      checkGroup(assets.vehicles, AssetType.Vehicles),
      checkGroup(assets.motorcycles, AssetType.Motorcycles),
      checkGroup(assets.caravans, AssetType.Caravans),
      checkGroup(assets.buildings, AssetType.Buildings),
      checkGroup(assets.contents, AssetType.Contents),
      checkGroup(assets.all_risks, AssetType.AllRisks),
      checkGroup(assets.specified_items, AssetType.SpecifiedItems),
    ]).then(() => { this.assetsCompleteInProgress = null; });

    return this.assetsCompleteInProgress;
  }

  async setSchedules() {
    try {
      const schedules = await this.busnQuoteScheduleService.getAllActiveSchedules();
      this.sharedConfig.setSchedulesActive(schedules);
      this.sharedConfigService.setSchedulesActive(schedules);

      if (this.sharedConfig.schedulesActive && this.sharedConfig.schedulesActive.length > 0)
          SharedConfig.schedulesPremiumDetails = this.sharedQuoteScheduleService.getSchedulesTotalPremium(this.sharedConfig.schedulesActive);
      const latestQuoteRequest =
        this.sharedConfig.quoteRequests?.length > 0
          ? this.sharedConfig.quoteRequests.reduce((prev, current) => (prev.id > current.id ? prev : current))
          : null;
      if (
        SharedConfig.showOnTrackPopup ||
        !this.sharedConfig.schedulesActive ||
        this.sharedConfig.schedulesActive.length === 0 ||
        (latestQuoteRequest &&
          latestQuoteRequest?.type === QuoteRequestType.Quote &&
          latestQuoteRequest?.status === QuoteRequestStatusType.Pending) ||
        SharedConfig.showSetNewPasswordComponent
      ) {
        SharedConfig.showFooterTabs = false;
      } else {
        SharedConfig.showFooterTabs = true;
      }

      this.checkIfWalletPageOnTrackPopupShouldBeHidden();

      SharedConfig.insured_vehicles = [];
      SharedConfig.insured_contents = [];
      SharedConfig.insured_buildings = [];
      SharedConfig.insured_motorcycles = [];
      SharedConfig.insured_caravans = [];
      SharedConfig.insured_vaps = [];
      SharedConfig.insured_all_risk = [];
      SharedConfig.insured_specified_items = [];

      for (const schedulesActive of this.sharedConfig.schedulesActive) {
        for (const vehicle of schedulesActive.insured_assets?.vehicles || []) {
          vehicle.policy_id = schedulesActive.policy_id;
          SharedConfig.insured_vehicles.push(vehicle);
        }
        for (const contents of schedulesActive.insured_assets?.contents || []) {
          contents.policy_id = schedulesActive.policy_id;
          SharedConfig.insured_contents.push(contents);
        }
        for (const buildings of schedulesActive.insured_assets?.buildings || []) {
          buildings.policy_id = schedulesActive.policy_id;
          SharedConfig.insured_buildings.push(buildings);
        }
        for (const motorcycles of schedulesActive.insured_assets?.motorcycles || []) {
          motorcycles.policy_id = schedulesActive.policy_id;
          SharedConfig.insured_motorcycles.push(motorcycles);
        }
        for (const caravans of schedulesActive.insured_assets?.caravans || []) {
          caravans.policy_id = schedulesActive.policy_id;
          SharedConfig.insured_caravans.push(caravans);
        }
        for (const all_risk of schedulesActive.insured_assets?.all_risks || []) {
          all_risk.policy_id = schedulesActive.policy_id;
          SharedConfig.insured_all_risk.push(all_risk);
        }
        for (const specified_items of schedulesActive.insured_assets?.specified_items || []) {
          specified_items.policy_id = schedulesActive.policy_id;
          SharedConfig.insured_specified_items.push(specified_items);
        }
        for (const vaps of schedulesActive.insured_assets?.vaps || []) {
          vaps.policy_id = schedulesActive.policy_id;
          SharedConfig.insured_vaps.push(vaps);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  // showOrHideOnTrackPopup(userAllRelatedData: IUserAllRelatedData) {
  //   const latestQuoteRequest =
  //     userAllRelatedData.quote_requests?.length > 0
  //       ? userAllRelatedData.quote_requests.reduce((prev, current) => (prev.id > current.id ? prev : current))
  //       : null;

  //   if (
  //     !(
  //       (latestQuoteRequest &&
  //         (latestQuoteRequest.status === QuoteRequestStatusType.Pending || latestQuoteRequest.status === QuoteRequestStatusType.WaitingOnInsurer) &&
  //         latestQuoteRequest.type === QuoteRequestType.Quote) ||
  //       (userAllRelatedData.schedules &&
  //         userAllRelatedData.schedules?.length === 1 &&
  //         userAllRelatedData.schedules[0].status !== 'ACTIVE' &&
  //         !SharedConfig.showOnTrackPopup)
  //     )
  //   ) {
  //     console.log('Stopping timer: Conditions no longer met');
  //     SharedConfig.showOnTrackPopup = false;
  //     this.sharedDialogService.hidePopupDialog();
  //   }
  // }

  checkIfWalletPageOnTrackPopupShouldBeHidden() {
    if (
      !(SharedConfig.showOnTrackPopup && SharedConfig.scheduleHasBeenSetToActive) &&
      SharedConfig.quoteRequest?.status !== QuoteRequestStatusType.Pending &&
      (SharedConfig.selectedPage === 'wallet' || SharedConfig.selectedPage === 'home-quote')
    ) {
      this.sharedDialogService.hidePopupDialog();
    }
  }

  setWalletQuoteButton() {
    SharedConfig.quoteStatusButtonText = 'Start a new quote';
    if (this.sharedConfig.quoteRequest) {
      switch (this.sharedConfig?.quoteRequest?.status) {
        case QuoteRequestStatusType.Open:
          SharedConfig.quoteStatusButtonText = 'Continue my quote';
          break;
        case QuoteRequestStatusType.Submitted:
          SharedConfig.quoteStatusButtonText = 'Busy getting quotes';
          break;
        case QuoteRequestStatusType.Pending:
          SharedConfig.quoteStatusButtonText = 'Loading your policy';
          break;
        case QuoteRequestStatusType.WaitingOnInsurer:
          SharedConfig.quoteStatusButtonText = 'Waiting for Insurer';
          break;
        case QuoteRequestStatusType.WaitingOnQuotes:
          SharedConfig.quoteStatusButtonText = 'View my quote';
          break;
        case QuoteRequestStatusType.WaitingToAccept:
          SharedConfig.quoteStatusButtonText = 'View my quotes';
          break;
        default:
      }
    }
  }

  async updateUserItc(): Promise<void> {
    const timeStamp = new Date().getTime();
    this.sharedConfig.user.account_details.itc_check = true;
    this.sharedConfig.user.account_details.ctrl_consent_check = true;
    this.sharedConfig.user.account_details.itc_check_timestamp = timeStamp;
    this.sharedConfig.user.account_details.ctrl_consent_timestamp = timeStamp;

    firstValueFrom(
      this.apiUserService.updateUserAccountDetails({
        id: this.sharedConfig.user.account_details.id,
        itc_check: true,
        ctrl_consent_check: true,
        itc_check_timestamp: timeStamp,
        ctrl_consent_timestamp: timeStamp,
      })
    );
  }

  async getDocuments(): Promise<any[]> {
    const result = (await firstValueFrom(this.apiUserService.getDocuments())).results;
    return result['files'];
  }

  // TODO Has Role access check
  hasAccess(): boolean {
    return false;
  }
}
